import {useState, useEffect, useReducer} from 'react';

import {
  useIntl,
  FormattedMessage,
  defineMessages,
  MessageDescriptor,
} from 'react-intl';
import {useDebounce} from 'react-use';
import styled from 'styled-components';

import {ICoach} from '@stryd/models';
import {colors, dimensions, SearchInput} from '@stryd/react-ui';
import {sameValues, removeValue} from '@stryd/util-lib';

import {coachFilterEvent} from 'src/utils/analytics/events';
import {track} from 'src/utils/analytics/gtag';

import CheckBoxesFilter from './CheckBoxesFilter';
import PlacesAutoCompleteFilter from './PlacesAutoCompleteFilter';
import ToggleFilter from './ToggleFilter';
import {platformOptions} from './trainingPlatforms';

const FilterBar = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  padding: 10px 40px;
  z-index: 3;
  @media screen and (max-width: ${dimensions.breakpoints.desktopSmall}) {
    flex-flow: column nowrap;
    align-items: center;
    justify-content: center;
    & > div:last-child {
      width: 50%;
    }
  }
  @media screen and (max-width: ${dimensions.breakpoints.mobile}) {
    & > div:last-child {
      width: 100%;
    }
  }
`;

const LeftContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: center;
  @media screen and (max-width: ${dimensions.breakpoints.desktopSmall}) {
    width: 100%;
    flex-flow: column nowrap;
  }
`;

const FilterOptions = styled.div`
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  justify-content: flex-start;
  @media screen and (max-width: ${dimensions.breakpoints.desktopSmall}) {
    margin-bottom: 20px;
  }
  @media screen and (max-width: ${dimensions.breakpoints.mobile}) {
    width: 100%;
    flex-flow: column nowrap;
    align-items: center;
    justify-content: center;
  }
`;

const FilterReset = styled.div`
  font-size: 16px;
  color: ${colors.themes.dark.textHighlight};
  text-align: center;
  display: flex;
  align-items: center;
  transition: 0.2s background-color, 0.2s color;
  &:hover {
    cursor: pointer;
    color: ${colors.themes.dark.accentPrimary};
  }
  @media screen and (max-width: ${dimensions.breakpoints.tabletLandscape}) {
    margin-bottom: 10px;
  }
`;

type Actions =
  | 'CLOSE_ALL_MENUS'
  | 'RESET_ALL_FILTERS'
  | 'UPDATE_FILTER'
  | 'FILTER_COACHES'
  | 'SEARCH_COACHES';

export type Option = {
  value: string;
  labelDescriptor: MessageDescriptor;
};

export type OptionGroup = {
  groupId: number;
  groupLabelDescriptor: MessageDescriptor;
  options: Option[];
};

export type Filter = {
  buttonLabelDescriptor: MessageDescriptor;
  labelDescriptor: string;
  type: string;
  active: boolean;
  menuOpen: boolean;
  value: any;
  createFilter: (value: any) => void;
};

type State = {
  filters: {
    [key: string]: Filter;
  };
  filterPipe?: any;
};

interface CoachesFilterAction {
  type: string;
  filterId?: string;
  update?: any;
}

interface CloseAllMenusAction extends CoachesFilterAction {
  type: 'CLOSE_ALL_MENUS';
}
interface ResetAllFiltersAction extends CoachesFilterAction {
  type: 'RESET_ALL_FILTERS';
}
interface UpdateFilterAction extends CoachesFilterAction {
  type: 'UPDATE_FILTER';
}
interface FilterCoachesAction extends CoachesFilterAction {
  type: 'FILTER_COACHES';
}
interface SearchCoachesAction extends CoachesFilterAction {
  type: 'SEARCH_COACHES';
}

type CoachesFilterActionType =
  | CloseAllMenusAction
  | ResetAllFiltersAction
  | UpdateFilterAction
  | FilterCoachesAction
  | SearchCoachesAction;

const actions: {[key: string]: Actions} = {
  closeAllMenus: 'CLOSE_ALL_MENUS',
  resetAllFilters: 'RESET_ALL_FILTERS',
  updateFilter: 'UPDATE_FILTER',
  filterCoaches: 'FILTER_COACHES',
  searchCoaches: 'SEARCH_COACHES',
};

const runningOptionsMessages = defineMessages({
  short: {
    id: 'CoachesFilter.runningOption.short',
    defaultMessage: 'Short (1mi - 10k)',
  },
  long: {
    id: 'CoachesFilter.runningOption.long',
    defaultMessage: 'Long (10k - Marathon)',
  },
  ultra: {
    id: 'CoachesFilter.runningOption.ultra',
    defaultMessage: 'Ultra (> Marathon)',
  },
});

const triathlonMessagesOptions = defineMessages({
  sprint: {
    id: 'CoachesFilter.triOption.sprint',
    defaultMessage: 'Sprint',
  },
  olympic: {
    id: 'CoachesFilter.triOption.olympic',
    defaultMessage: 'Olympic',
  },
  halfIronman: {
    id: 'CoachesFilter.triOption.halfIronman',
    defaultMessage: 'Half Ironman',
  },
  ironman: {
    id: 'CoachesFilter.triOption.ironman',
    defaultMessage: 'Full Ironman',
  },
});

const platformMessageDescriptors = defineMessages({
  power_center: {
    id: 'CoachesFilter.platform.powercenter',
    defaultMessage: 'Stryd PowerCenter',
  },
  training_peaks: {
    id: 'CoachesFilter.platform.training_peaks',
    defaultMessage: 'Training Peaks',
  },
  final_surge: {
    id: 'CoachesFilter.platform.final_surge',
    defaultMessage: 'Final Surge',
  },
  two_peak: {
    id: 'CoachesFilter.platform.two_peak',
    defaultMessage: '2 Peak',
  },
});

/*
   When adding filters, make sure to update the following:
   1. filterIds
   2. filterLabelDescriptors
   3. filterOptions
   4. defaultValues
   5. filterTypes (if necessary)
   6. initialState
   7. filterChangeFunctions (if necessary)
*/
const filterIds = {
  runPrefs: 'runPrefs',
  distance: 'distance',
  trainingPlatforms: 'trainingPlatforms',
  inPerson: 'inPerson',
  location: 'location',
  nameSearch: 'nameSearch',
};

const filterLabelDescriptors = defineMessages({
  runPrefs: {
    id: 'CoachesFilter.filterLabel.runPrefs',
    defaultMessage: 'Run Preferences',
  },
  distance: {
    id: 'CoachesFilter.filterLabel.distance',
    defaultMessage: 'Distance',
  },
  trainingPlatforms: {
    id: 'CoachesFilter.filterLabel.trainingPlatforms',
    defaultMessage: 'Coaching Platforms',
  },
  inPerson: {
    id: 'CoachesFilter.filterLabel.inPerson',
    defaultMessage: 'In Person',
  },
  location: {
    id: 'CoachesFilter.filterLabel.location',
    defaultMessage: 'Country',
  },
  nameSearch: {
    id: 'CoachesFilter.filterLabel.nameSearch',
    defaultMessage: 'Search',
  },
});

const filterOptionGroupLabelDescriptors = defineMessages({
  running: {
    id: 'CoachesFilter.distanceGroupLabel.running',
    defaultMessage: 'Running',
  },
  triathlon: {
    id: 'CoachesFilter.distanceGroupLabel.triathlon',
    defaultMessage: 'Triathlon',
  },
  platforms: {
    id: 'CoachesFilter.platformsGroupLabel',
    defaultMessage: 'Platforms',
  },
});

const filterOptions = {
  [filterIds.distance]: [
    {
      groupId: 0,
      groupLabelDescriptor: filterOptionGroupLabelDescriptors.running,
      options: [
        {value: 'short', labelDescriptor: runningOptionsMessages.short},
        {value: 'long', labelDescriptor: runningOptionsMessages.long},
        {value: 'ultra', labelDescriptor: runningOptionsMessages.ultra},
      ],
    },
    {
      groupId: 1,
      groupLabelDescriptor: filterOptionGroupLabelDescriptors.triathlon,
      options: [
        {value: 'sprint', labelDescriptor: triathlonMessagesOptions.sprint},
        {value: 'olympic', labelDescriptor: triathlonMessagesOptions.olympic},
        {
          value: 'half ironman',
          labelDescriptor: triathlonMessagesOptions.halfIronman,
        },
        {
          value: 'full ironman',
          labelDescriptor: triathlonMessagesOptions.ironman,
        },
      ],
    },
  ],
  [filterIds.trainingPlatforms]: [
    {
      groupId: 0,
      groupLabelDescriptor: filterOptionGroupLabelDescriptors.platforms,
      options: [
        {
          value: platformOptions.power_center,
          labelDescriptor: platformMessageDescriptors.power_center,
        },
        {
          value: platformOptions.training_peaks,
          labelDescriptor: platformMessageDescriptors.training_peaks,
        },
        {
          value: platformOptions.final_surge,
          labelDescriptor: platformMessageDescriptors.final_surge,
        },
        {
          value: platformOptions.two_peak,
          labelDescriptor: platformMessageDescriptors.two_peak,
        },
      ],
    },
  ],
  [filterIds.inPerson]: [],
  [filterIds.location]: [],
  [filterIds.nameSearch]: [],
};

const defaultValues = {
  [filterIds.distance]: [],
  [filterIds.trainingPlatforms]: [],
  [filterIds.inPerson]: false,
  [filterIds.location]: '',
  [filterIds.nameSearch]: '',
};

const filterTypes = {
  checkbox: 'checkbox',
  toggle: 'toggle',
  text: 'text',
  search: 'search',
};

const initialState = {
  filters: {
    [filterIds.distance]: {
      buttonLabelDescriptor: filterLabelDescriptors.distance,
      labelDescriptor: '',
      type: filterTypes.checkbox,
      active: false,
      menuOpen: false,
      value: defaultValues[filterIds.distance],
      createFilter: (checkedBoxes: any) => {
        return (data: ICoach[]) =>
          data.filter((coach) => {
            if (
              sameValues(checkedBoxes, defaultValues[filterIds.distance] as any)
            ) {
              return true;
            }
            for (const distanceType of checkedBoxes) {
              if (
                coach.distance_types.includes(distanceType) ||
                coach.triathlon_types.includes(distanceType)
              ) {
                return true;
              }
            }
            return false;
          });
      },
    },
    [filterIds.trainingPlatforms]: {
      buttonLabelDescriptor: filterLabelDescriptors.trainingPlatforms,
      labelDescriptor: '',
      type: filterTypes.checkbox,
      active: false,
      menuOpen: false,
      value: defaultValues[filterIds.trainingPlatforms],
      createFilter: (checkedBoxes: any) => {
        return (data: ICoach[]) =>
          data.filter((coach) => {
            if (checkedBoxes.length === 0) {
              return true;
            }
            for (const platform of checkedBoxes) {
              if (coach.training_platforms.includes(platform)) {
                return true;
              }
            }
            return false;
          });
      },
    },
    [filterIds.location]: {
      buttonLabelDescriptor: filterLabelDescriptors.location,
      labelDescriptor: '',
      type: filterTypes.text,
      active: false,
      menuOpen: false,
      value: defaultValues[filterIds.location],
      createFilter: (location: any) => {
        return (data: ICoach[]) =>
          data.filter((coach) => {
            if (location.length === 0) {
              return true;
            }
            if (coach.country.toLowerCase() === location.toLowerCase()) {
              return true;
            }
            return false;
          });
      },
    },
    [filterIds.inPerson]: {
      buttonLabelDescriptor: filterLabelDescriptors.inPerson,
      labelDescriptor: filterLabelDescriptors.inPerson,
      type: filterTypes.toggle,
      active: false,
      menuOpen: false,
      value: defaultValues[filterIds.inPerson],
      createFilter: (toggleValue: any) => {
        return (data: ICoach[]) =>
          data.filter((coach) => {
            if (!toggleValue) return true;
            if (coach.in_person === toggleValue) return true;
            return false;
          });
      },
    },
    [filterIds.nameSearch]: {
      type: filterTypes.search,
      active: false,
      value: defaultValues[filterIds.nameSearch],
      createFilter: (searchValue: any) => {
        return (data: ICoach[]) =>
          data.filter((coach) => {
            let name = '';
            if (coach.prefix.length > 0) {
              name += coach.prefix.toLowerCase() + ' ';
            }
            name += coach.first_name.toLowerCase() + ' ';
            if (coach.middle_name.length > 0) {
              name += coach.middle_name.toLowerCase() + ' ';
            }
            name += coach.last_name.toLowerCase();

            if (name.includes(searchValue.toLowerCase())) {
              return true;
            }
            return false;
          });
      },
    },
  },
  filterPipe: () => {
    //set initial filterPipe
    const {filters} = initialState;
    const filterIds = Object.keys(filters);
    const filterFns = filterIds.map((id) => {
      const {value, createFilter} = filters[id];
      return createFilter(value);
    });

    return createPipe(...filterFns);
  },
};

const resetAllFilters = (filters: {[key: string]: Filter}) => {
  const defaultFilters = {...filters};
  const filterIds = Object.keys(defaultFilters);
  filterIds.forEach((filterId) => {
    defaultFilters[filterId].active = false;
    defaultFilters[filterId].value = defaultValues[filterId];
  });
  return defaultFilters;
};

//...args = coaches array
const compose =
  (f: any, g: any) =>
  (...args: ICoach[]) =>
    g(f(...args));
const createPipe = (...fns: any) => fns.reduce(compose);

const checkActive = (filterId: string, state: State, {value}: any) => {
  //check if update from dispatch isn't for value
  if (
    (value === null || value === undefined) &&
    !checkActive(filterId, state, state.filters[filterId])
  ) {
    return false;
  }
  const defaultValue = defaultValues[filterId];
  const filterValue =
    value !== null || value !== undefined
      ? value
      : state.filters[filterId].value;

  //Check if array
  if (Array.isArray(filterValue)) {
    return !sameValues(filterValue, defaultValue as any);
  } else {
    //Non-array values
    if (filterValue === defaultValue) {
      return false;
    }
    return true;
  }
};

const closeAllMenus = (filters: {[key: string]: Filter}) => {
  const filtersCopy = {...filters};
  const filterIds = Object.keys(filtersCopy);
  filterIds.forEach((filterId) => {
    filtersCopy[filterId].menuOpen = false;
  });

  return filtersCopy;
};

const filterReducer = (
  state: State,
  action: CoachesFilterActionType
): State => {
  switch (action.type) {
    case actions.updateFilter: {
      const {filterId, update} = action;
      if (!filterId) {
        return {...state};
      }

      return {
        ...state,
        filters: {
          ...state.filters,
          [filterId]: {
            ...state.filters[filterId],
            ...update,
          },
        },
      };
    }
    case actions.filterCoaches: {
      const {filters} = state;
      const filterIds = Object.keys(filters);

      const filterFns = filterIds.map((id) => {
        const {value, createFilter} = filters[id];
        return createFilter(value);
      });

      const filterPipe = createPipe(...filterFns);
      const activatedFilters = {filters: {...state.filters}};
      filterIds.forEach((id) => {
        activatedFilters.filters = {
          ...activatedFilters.filters,
          [id]: {
            ...activatedFilters.filters[id],
            active: checkActive(id, state, {
              value: activatedFilters.filters[id].value,
            }),
          },
        };
      });

      return {
        ...state,
        ...activatedFilters,
        filterPipe: filterPipe,
      };
    }
    case actions.resetAllFilters: {
      return {
        ...state,
        filters: {...resetAllFilters(state.filters)},
      };
    }

    case actions.closeAllMenus: {
      return {
        ...state,
        filters: {...closeAllMenus(state.filters)},
      };
    }
    default: {
      throw new Error(`Unsupported action type: ${action.type}`);
    }
  }
};

const trackFilters = (state: State) => {
  const params = {
    distance: state.filters.distance.value,
    training_platforms: state.filters.trainingPlatforms.value,
    name: state.filters.nameSearch.value,
    in_person: state.filters.inPerson.value,
    location: state.filters.location.value,
  };
  if (
    params.distance.length === 0 &&
    params.training_platforms.length === 0 &&
    !params.location &&
    !params.name &&
    !params.in_person
  )
    return;
  track(coachFilterEvent(params));
};

type CoachesFilterProps = {
  onFilterChange: (filterPipe: any) => void;
};

const CoachesFilter: React.FC<React.PropsWithChildren<CoachesFilterProps>> = ({
  onFilterChange,
}) => {
  const [state, dispatch] = useReducer(filterReducer, initialState as State);
  const intl = useIntl();
  const [name, setName] = useState('');

  useDebounce(
    () => {
      dispatch({
        type: actions.updateFilter,
        filterId: filterIds.nameSearch,
        update: {
          value: name,
        },
      });
      dispatch({
        type: actions.filterCoaches,
      });
    },
    300,
    [name]
  );

  useEffect(() => {
    if (state.filterPipe) {
      trackFilters(state);
      onFilterChange(state.filterPipe);
    }
    // eslint-disable-next-line
  }, [state.filterPipe, onFilterChange]);

  const filterChangeFunctions = {
    handleCheckBoxChange: ({
      checked,
      newValue,
      filterId,
    }: {
      checked: boolean;
      newValue: string;
      filterId: string;
    }) => {
      if (checked) {
        dispatch({
          type: actions.updateFilter,
          filterId: filterId,
          update: {
            value: [...state.filters[filterId].value, newValue],
          },
        });
      } else {
        dispatch({
          type: actions.updateFilter,
          filterId: filterId,
          update: {
            value: removeValue(state.filters[filterId].value, newValue),
          },
        });
      }
    },
    handleToggleChange: ({
      isActive,
      filterId,
    }: {
      isActive: boolean;
      filterId: string;
    }) => {
      dispatch({
        type: actions.updateFilter,
        filterId: filterId,
        update: {
          value: isActive,
        },
      });
    },
    handleTextChange: (
      event: {target: {value: string; label: string}},
      filterId: string
    ) => {
      const update = {
        value: event.target.value,
        label: event.target.value,
      };
      if (event.target.label) update.label = event.target.label;
      dispatch({
        type: actions.updateFilter,
        filterId: filterId,
        update: update,
      });
    },
  };

  const onMenuButtonClick = (filterId: string) => {
    dispatch({
      type: actions.closeAllMenus,
    });
    dispatch({
      type: actions.updateFilter,
      filterId: filterId,
      update: {menuOpen: !state.filters[filterId].menuOpen},
    });
  };

  const onOutsideClick = (filterId: string) => {
    dispatch({
      type: actions.updateFilter,
      filterId: filterId,
      update: {menuOpen: false},
    });
  };

  const onSaveButtonClick = () => {
    dispatch({
      type: actions.filterCoaches,
    });
    dispatch({
      type: actions.closeAllMenus,
    });
  };

  const onResetButtonClick = (filterId: string) => {
    dispatch({
      type: actions.updateFilter,
      filterId: filterId,
      update: {menuOpen: false, value: defaultValues[filterId]},
    });
    dispatch({
      type: actions.filterCoaches,
    });
  };

  useEffect(() => {
    const handleMenuEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        dispatch({
          type: actions.closeAllMenus,
        });
        dispatch({
          type: actions.filterCoaches,
        });
      }
    };

    window.addEventListener('keydown', handleMenuEscape);
    return () => {
      window.removeEventListener('keydown', handleMenuEscape);
    };
  }, []);

  return (
    <FilterBar>
      <LeftContainer>
        <FilterOptions>
          {Object.keys(state.filters).map((filterId) => {
            switch (state.filters[filterId].type) {
              case filterTypes.checkbox: {
                return (
                  <CheckBoxesFilter
                    key={'filter_' + filterId}
                    filterId={filterId}
                    filter={state.filters[filterId]}
                    onOutsideClick={() => {
                      onOutsideClick(filterId);
                    }}
                    onMenuButtonClick={() => {
                      onMenuButtonClick(filterId);
                    }}
                    onFilterChange={({checked, value}) => {
                      filterChangeFunctions.handleCheckBoxChange({
                        checked: checked,
                        newValue: value,
                        filterId: filterId,
                      });
                    }}
                    onSaveButtonClick={onSaveButtonClick}
                    onResetButtonClick={() => onResetButtonClick(filterId)}
                    optionGroups={filterOptions[filterId]}
                  />
                );
              }
              case filterTypes.text: {
                return (
                  <PlacesAutoCompleteFilter
                    key={'filter_' + filterId}
                    filterId={filterId}
                    placeholder={intl.formatMessage({
                      id: 'CoachesFilter.location.placeholder',
                      defaultMessage: 'Enter a country',
                    })}
                    filter={state.filters[filterId]}
                    onOutsideClick={() => {
                      onOutsideClick(filterId);
                    }}
                    onMenuButtonClick={() => {
                      onMenuButtonClick(filterId);
                    }}
                    onFilterChange={(event) => {
                      //Handles AutoComplete special case
                      if (event.data) {
                        filterChangeFunctions.handleTextChange(
                          {
                            target: {
                              value: event.data.value,
                              label: event.data.label,
                            },
                          },
                          filterId
                        );
                      } else {
                        filterChangeFunctions.handleTextChange(event, filterId);
                      }
                    }}
                    onSaveButtonClick={onSaveButtonClick}
                    onResetButtonClick={onResetButtonClick}
                  />
                );
              }
              case filterTypes.toggle: {
                return (
                  <ToggleFilter
                    key={'filter_' + filterId}
                    onOutsideClick={() => {
                      return onOutsideClick(filterId);
                    }}
                    onMenuButtonClick={() => {
                      onMenuButtonClick(filterId);
                    }}
                    onFilterChange={(isActive) => {
                      filterChangeFunctions.handleToggleChange({
                        isActive,
                        filterId,
                      });
                      dispatch({
                        type: actions.filterCoaches,
                      });
                    }}
                    onSaveButtonClick={onSaveButtonClick}
                    filterId={filterId}
                    filter={state.filters[filterId]}
                  />
                );
              }
              case filterTypes.search: {
                //Empty case because the coach searchBar is considered a filter.
                return null;
              }
              default: {
                throw new Error(
                  'Invalid filter type: ' + state.filters[filterId].type
                );
              }
            }
          })}
          <FilterReset
            onClick={() => {
              dispatch({
                type: actions.resetAllFilters,
              });
              dispatch({
                type: actions.filterCoaches,
              });
              setName('');
            }}
          >
            <FormattedMessage
              id="CoachesFilter.resetAction"
              defaultMessage="Reset Filters"
            />
          </FilterReset>
        </FilterOptions>
      </LeftContainer>
      <SearchInput
        id="coach-search"
        placeholder={intl.formatMessage({
          id: 'CoachesFilter.searchPlaceholder',
          defaultMessage: 'Search for a coach',
        })}
        value={name}
        label="Search"
        submitButton={false}
        inputStyle={{
          backgroundColor: colors.themes.dark.backgroundInverted,
          color: colors.themes.dark.textInputs,
          borderRadius: '8px',
        }}
        onChange={(event) => {
          setName(event.target.value);
        }}
        onSubmit={(event) => {
          event.preventDefault();
        }}
        width={'100%'}
      />
    </FilterBar>
  );
};

export default CoachesFilter;
